import React, { useEffect, useRef, useState, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { NavLink } from "react-router-dom";
import SubNav from "../components/nav";
import SEO from '../components/SEO';
import { ArrowRight, PenTool, BookOpen, Edit3, Download, BarChart2 } from 'lucide-react';
import '../styles/BudgetPreProduction.css';
import { baseUrl } from '../config';



// Section component remains the same
const Section = memo(({ section, isActive, refCallback }) => (
    <div
        ref={refCallback}
        className={`how-to-use-budget__section ${isActive ? 'how-to-use-budget__animate-in' : ''}`}
        aria-label={section.title}
        tabIndex={0}
    >
        <div className="how-to-use-budget__section-icon">{section.icon}</div>
        <div className="how-to-use-budget__section-content">
            <h2 className="how-to-use-budget__section-title">{section.title}</h2>
            <p className="how-to-use-budget__section-text">{section.content}</p>
            <p className="how-to-use-budget__section-details">{section.details}</p>
        </div>
        <img
            className="how-to-use-budget__section-image"
            src={section.img}
            alt={`${section.title} illustration`}
        />
        {section.iframe}
    </div>
));

Section.propTypes = {
    section: PropTypes.shape({
        title: PropTypes.string.isRequired,
        content: PropTypes.string.isRequired,
        details: PropTypes.string.isRequired,
        icon: PropTypes.element.isRequired,
        img: PropTypes.string.isRequired,
        iframe: PropTypes.element.isRequired,
    }).isRequired,
    isActive: PropTypes.bool.isRequired,
    refCallback: PropTypes.func.isRequired,
};

const HowToUseBudgetPreProduction = () => {
    const sectionRefs = useRef([]);
    const [activeSection, setActiveSection] = useState(0);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const index = sectionRefs.current.indexOf(entry.target);
                        setActiveSection(index);
                    }
                });
            },
            { threshold: 0.3 }
        );

        const currentRefs = sectionRefs.current;
        currentRefs.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            currentRefs.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
            observer.disconnect();
        };
    }, []);

    // Add this new function to handle clicking on sidebar items
    const handleSectionClick = (index) => {
        const element = sectionRefs.current[index];
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
            setActiveSection(index);
        }
    };

    const sections = useMemo(() => [
        {
            title: "Generate Budget Estimates",
            content: `Upload your script or project details, and the AI will generate an initial budget estimate based on historical data and market rates. This gives you a solid starting point for your project's financial planning.`,
            details: `Tip: Provide as much detail as possible about your project scope, crew, locations, and other relevant factors to get the most accurate budget estimates.`,
            icon: <PenTool size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="generate-budget-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Edit and Customize Budget",
            content: `The generated budget is fully editable, allowing you to adjust individual line items, add custom categories, and modify rates to match your project's specific needs. This flexibility ensures the budget accurately reflects your unique requirements.`,
            details: `Example: If you need to account for specialized equipment or location costs, you can easily add those as custom line items.`,
            icon: <BookOpen size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="edit-budget-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Visualize Budget Data",
            content: `Utilize interactive bar graphs and pie charts to easily visualize budget allocations across different categories. This feature makes it convenient to review and analyze costs at a glance, helping you make informed decisions about resource allocation.`,
            details: `Tip: Pay close attention to the relative distribution of costs across different line items. This can reveal areas where you may need to adjust your budget.`,
            icon: <Edit3 size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="visualize-budget-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Download Budget Reports",
            content: `Once you're satisfied with the budget, you can download the data in various formats (e.g., CSV, PDF) for sharing with stakeholders or further analysis. This ensures your budget information can be seamlessly integrated into your project management workflows.`,
            details: `Note: The downloaded reports can be used for pitching, grant applications, or internal financial planning.`,
            icon: <Download size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="download-budget-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        },
        {
            title: "Analyze Budget Metrics",
            content: `Our analytics tools offer valuable insights into your budget's structure. Analyze key metrics like total cost, cost per unit, and budget allocation percentages. This data helps you identify areas for optimization and ensure your project stays on track financially.`,
            details: `For instance, if you notice that one category is consuming a disproportionately high percentage of the overall budget, you can investigate ways to reduce those costs.`,
            icon: <BarChart2 size={24} />,
            img: 'https://via.placeholder.com/150',
            iframe: <iframe title="analyze-budget-video" src="https://www.youtube.com/embed/_GuOjXYl5ew" frameBorder="0" allowFullScreen></iframe>
        }
    ], []);

    return (
        <>
            <SEO
              title="Film Budget Guide"
              description="Master film budgeting with ProducerBuddy's step-by-step guide. Break down production costs, track spending, and optimise your film budget."
              url="https://producerbuddy.pilomation.com/budgetHowTo"
            />
            <SubNav />
            <div className="how-to-use-budget">
                <div className="how-to-use-budget__container">
                    <h1 className="how-to-use-budget__title">How to Use Producer Buddy: Budget Pre-Production</h1>

                    <div className="how-to-use-budget__layout">
                        {/* Updated Sidebar */}
                        <div className="how-to-use-budget__sidebar" role="navigation" aria-label="Section navigation">
                            {sections.map((section, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSectionClick(index)}
                                    className={`how-to-use-budget__sidebar-item ${activeSection === index ? 'active' : ''}`}
                                    aria-current={activeSection === index ? 'page' : undefined}
                                    type="button"
                                >
                                    <div className="flex items-center justify-between w-full">
                                        <span>{section.title}</span>
                                        {activeSection === index && (
                                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Timeline remains the same */}
                        <div className="how-to-use-budget__timeline">
                            {sections.map((section, index) => (
                                <Section
                                    key={index}
                                    section={section}
                                    isActive={activeSection === index}
                                    refCallback={(el) => (sectionRefs.current[index] = el)}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Call to action remains the same */}
                    <div className="how-to-use-budget__cta-container">
                        <NavLink to="/Services" className="how-to-use-budget__cta-button">
                            Explore Features
                            <ArrowRight className="ml-2" />
                        </NavLink>
                    </div>
                </div>
            </div>
        </>
    );
};

export default HowToUseBudgetPreProduction;
